import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {isAxiosError} from 'axios'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {translateApiError} from '@/lib/errors'
import {formatCopFull} from '@/lib/money'
import {formatSegmentKey} from '@/lib/segments'
import {
  fetchUpgradePreview,
  useCreateUpgrade,
  type UpgradePreview,
  type UpgradeProposal,
} from '@/hooks/inci/cartera/use-billing'

export type UpgradeKind = 'empleado' | 'riesgo' | 'conteo'

export type UpgradeGate = {kind: UpgradeKind; proposal: UpgradeProposal}

/** upgrade-gating-spec §1/§2/§8. After payment the caller retries the original act; the modal
 * never retries the mutation itself. */
export function UpgradeModal({
  businessId,
  gate,
  hasPaymentSource,
  onClose,
  onPaid,
}: {
  businessId: string
  gate: UpgradeGate | null
  hasPaymentSource: boolean
  onClose: () => void
  onPaid: (proposedBand: string) => void
}) {
  const {t} = useTranslation()
  const [preview, setPreview] = useState<UpgradePreview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [paymentOpened, setPaymentOpened] = useState(false)
  const createUpgrade = useCreateUpgrade(businessId)

  useEffect(() => {
    if (!gate) {
      setPreview(null)
      setError(null)
      setPaymentOpened(false)
      return
    }
    let cancelled = false
    setPreview(null)
    setError(null)
    fetchUpgradePreview(businessId, gate.proposal)
      .then((data) => {
        if (!cancelled) setPreview(data)
      })
      .catch(() => {
        if (!cancelled) setError(t('planFacturacion.upgradeModal.errorGeneric'))
      })
    return () => {
      cancelled = true
    }
  }, [gate, businessId, t])

  if (!gate) return null

  const iva = preview ? Math.round(preview.prorated_amount_cop * 0.19) : 0
  const total = preview ? preview.prorated_amount_cop + iva : 0
  const bodyKey = gate.kind === 'empleado' ? 'bodyEmpleado' : gate.kind === 'riesgo' ? 'bodyRiesgo' : 'bodyConteo'

  function pay() {
    if (!preview) return
    setError(null)
    createUpgrade.mutate(gate!.proposal, {
      onSuccess: (data) => {
        if (hasPaymentSource) {
          // Auto-débito: the backend charged the active source (COF); the webhook confirms it.
          onPaid(data.proposed_segment_key)
        } else if (data.payment_url) {
          setPaymentOpened(true)
          window.open(data.payment_url, '_blank', 'noopener')
          onPaid(data.proposed_segment_key)
        } else {
          setError(t('planFacturacion.upgradeModal.errorGeneric'))
        }
      },
      onError: (err) => {
        if (isAxiosError(err) && err.response?.status === 503) {
          setError(t('planFacturacion.gatewayUnconfigured'))
        } else {
          // translateApiError marks the error self-handled (PYTHON-27): an expected 409 must not report
          // as unhandled nor stack a second toast.
          setError(translateApiError(err))
        }
      },
    })
  }

  return (
    <Dialog open={!!gate} onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="upgrade-modal">
        <DialogHeader>
          <DialogTitle>{t('planFacturacion.upgradeModal.title')}</DialogTitle>
          <DialogDescription>{t(`planFacturacion.upgradeModal.${bodyKey}`)}</DialogDescription>
        </DialogHeader>

        {error && (
          <p className="text-sm text-destructive" data-testid="upgrade-modal-error">
            {error}
          </p>
        )}

        {!preview && !error ? (
          <Skeleton className="h-40 w-full" />
        ) : preview ? (
          <div className="space-y-3">
            <div
              className="rounded-lg border border-border px-4 py-2 text-center text-sm text-foreground"
              data-testid="upgrade-modal-band">
              {t('planFacturacion.upgradeModal.bandLine', {
                current: formatSegmentKey(preview.current_segment_key, t),
                proposed: formatSegmentKey(preview.proposed_segment_key, t),
              })}
            </div>
            <dl className="space-y-1.5 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <dt>{t('planFacturacion.upgradeModal.currentPrice')}</dt>
                <dd className="font-mono tabular-nums">{formatCopFull(preview.current_price_cop)}</dd>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <dt>{t('planFacturacion.upgradeModal.proposedPrice')}</dt>
                <dd className="font-mono tabular-nums">{formatCopFull(preview.proposed_price_cop)}</dd>
              </div>
              <div className="flex justify-between text-foreground">
                <dt>{t('planFacturacion.upgradeModal.proratedLabel', {days: preview.days_remaining})}</dt>
                <dd className="font-mono tabular-nums" data-testid="upgrade-modal-prorated">
                  {formatCopFull(preview.prorated_amount_cop)}
                </dd>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <dt>{t('planFacturacion.upgradeModal.ivaLabel')}</dt>
                <dd className="font-mono tabular-nums">{formatCopFull(iva)}</dd>
              </div>
              <div className="flex justify-between border-t border-border pt-1.5 font-medium text-foreground">
                <dt>{t('planFacturacion.upgradeModal.totalLabel')}</dt>
                <dd className="font-mono tabular-nums" data-testid="upgrade-modal-total">
                  {formatCopFull(total)}
                </dd>
              </div>
            </dl>
            <p className="text-xs text-muted-foreground">{t('planFacturacion.upgradeModal.conceptNote')}</p>

            {paymentOpened && (
              <p className="text-sm text-muted-foreground" data-testid="upgrade-modal-retry-note">
                {t('planFacturacion.upgradeModal.paymentOpenedNote')}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose} disabled={createUpgrade.isPending}>
                {t('planFacturacion.upgradeModal.cancel')}
              </Button>
              <Button data-testid="upgrade-modal-pay" onClick={pay} disabled={createUpgrade.isPending}>
                {createUpgrade.isPending
                  ? t('common.saving')
                  : hasPaymentSource
                    ? t('planFacturacion.upgradeModal.confirmCta')
                    : t('planFacturacion.upgradeModal.payCta')}
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
